import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { TopBar } from "@/components/TopBar";
import { toast } from "sonner";
import { Upload, Sparkles, X, ArrowLeft, CheckCircle2, FileText, ClipboardCheck, GraduationCap, ListChecks, BookOpen, ScrollText, PenLine, Wand2 } from "lucide-react";

const TYPES = [
  { key: "Lesson", icon: BookOpen }, { key: "Quiz", icon: ListChecks }, { key: "Test", icon: GraduationCap },
  { key: "Review Sheet", icon: ClipboardCheck }, { key: "Study Guide", icon: FileText },
  { key: "Vocabulary", icon: ScrollText }, { key: "Essay", icon: PenLine },
];

async function fileToB64(file) {
  return new Promise((res) => { const r = new FileReader(); r.onload = () => res(String(r.result).split(",")[1]); r.readAsDataURL(file); });
}

export default function Studio() {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [form, setForm] = useState({ student_id: "", subject: "", chapter: "", reading_level: "7", grade_level: "", teacher_instructions: "" });
  const [files, setFiles] = useState([]);
  const [phase, setPhase] = useState("setup"); // setup | ready | review
  const [busy, setBusy] = useState(false);
  const [chapter, setChapter] = useState(null); // {chapter_id, chapter_title, preview}
  const [lesson, setLesson] = useState(null);
  const [activeType, setActiveType] = useState(null);
  const [created, setCreated] = useState([]);

  useEffect(() => { api.get("/students").then((r) => setStudents(r.data)); }, []);

  const pickStudent = (id) => {
    const s = students.find((x) => x.id === id);
    setForm((f) => ({ ...f, student_id: id, grade_level: String(s?.grade_level ?? ""), reading_level: String(s?.reading_level ?? "7") }));
    api.get(`/subjects?student_id=${id}`).then((r) => setSubjects(r.data));
  };

  const onFiles = async (fl) => {
    const arr = Array.from(fl);
    const withB64 = await Promise.all(arr.map(async (f) => ({ name: f.name, url: URL.createObjectURL(f), b64: await fileToB64(f) })));
    setFiles((p) => [...p, ...withB64]);
  };

  const extract = async () => {
    if (!form.student_id || !form.subject || !form.chapter) return toast.error("Choose a student, subject and chapter.");
    if (!files.length) return toast.error("Upload at least one textbook page photo.");
    setBusy(true);
    try {
      const { data } = await api.post("/studio/extract", { images: files.map((f) => f.b64), hint: `${form.subject} ${form.chapter}` });
      setChapter(data); setPhase("ready");
      toast.success(`Chapter read: ${data.chapter_title}`);
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const generate = async (type) => {
    setActiveType(type); setBusy(true);
    try {
      const { data } = await api.post("/studio/generate", { chapter_id: chapter.chapter_id, ...form, assignment_type: type });
      setLesson(data.lesson); setPhase("review");
      toast.success(`${type} generated — review it.`);
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const publish = async () => {
    setBusy(true);
    try {
      const title = `${activeType}: ${lesson.chapter_title || form.chapter}`;
      await api.post("/assignments", {
        student_id: form.student_id, subject: form.subject, chapter: form.chapter, title,
        reading_level: form.reading_level, lesson, teacher_instructions: form.teacher_instructions, published: true,
        original_images: chapter?.images || [],
      });
      setCreated((c) => [...c, { type: activeType, title }]);
      toast.success(`${activeType} published!`);
      setLesson(null); setActiveType(null); setPhase("ready");
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <TopBar subtitle="AI Studio" />
      <main className="max-w-4xl mx-auto px-6 md:px-8 py-10">
        <button onClick={() => navigate("/parent")} className="flex items-center gap-2 text-sm text-[#6B7280] hover:text-[#3D405B] mb-6"><ArrowLeft size={16} /> Back</button>
        <div className="flex items-center gap-3 mb-2">
          <span className="grid place-items-center w-11 h-11 rounded-2xl bg-[#E07A5F] text-white"><Wand2 size={20} /></span>
          <h1 className="font-head text-4xl font-bold text-[#3D405B]">AI Studio</h1>
        </div>
        <p className="text-[#6B7280] mb-8">Upload a chapter once, then spin up a whole week — reading, quiz, study guide, test and more.</p>

        {phase === "setup" && (
          <div className="space-y-6">
            <div className="grid md:grid-cols-2 gap-4">
              <div><label className="lbl">Student</label>
                <select data-testid="studio-student" value={form.student_id} onChange={(e) => pickStudent(e.target.value)} className="inp">
                  <option value="">Choose…</option>{students.map((s) => <option key={s.id} value={s.id}>{s.first_name}</option>)}
                </select></div>
              <div><label className="lbl">Subject</label>
                <select data-testid="studio-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="inp">
                  <option value="">Choose…</option>{subjects.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
                </select></div>
              <div><label className="lbl">Chapter</label>
                <input data-testid="studio-chapter" value={form.chapter} onChange={(e) => setForm({ ...form, chapter: e.target.value })} className="inp" placeholder="e.g. Chapter 12" /></div>
              <div><label className="lbl">Reading level</label>
                <input data-testid="studio-reading" value={form.reading_level} onChange={(e) => setForm({ ...form, reading_level: e.target.value })} className="inp" /></div>
            </div>
            <label data-testid="studio-dropzone" className="flex flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-[#EAE6DF] bg-white p-10 cursor-pointer hover:border-[#E07A5F] transition-colors">
              <Upload size={26} className="text-[#E07A5F]" />
              <span className="text-sm text-[#6B7280]">Add JPG / PNG photos of the chapter pages</span>
              <input type="file" accept="image/png,image/jpeg,image/webp" multiple className="hidden" onChange={(e) => onFiles(e.target.files)} />
            </label>
            {files.length > 0 && (
              <div className="flex flex-wrap gap-3">
                {files.map((f, i) => (
                  <div key={i} className="relative w-24 h-28 rounded-xl overflow-hidden border border-[#EAE6DF]">
                    <img src={f.url} alt="" className="w-full h-full object-cover" />
                    <button onClick={() => setFiles(files.filter((_, x) => x !== i))} className="absolute top-1 right-1 bg-white/90 rounded-full p-0.5"><X size={13} /></button>
                  </div>
                ))}
              </div>
            )}
            <button data-testid="studio-extract-btn" onClick={extract} disabled={busy}
              className="flex items-center gap-2 rounded-full bg-[#E07A5F] text-white px-8 py-3.5 font-semibold hover:bg-[#c96851] transition-colors disabled:opacity-60">
              <Sparkles size={18} /> {busy ? "Reading the chapter…" : "Read Chapter"}
            </button>
          </div>
        )}

        {phase === "ready" && chapter && (
          <div className="space-y-6">
            <div className="rounded-2xl bg-white border border-[#EAE6DF] p-6">
              <p className="text-xs font-semibold uppercase tracking-wide text-[#81B29A]">Chapter loaded</p>
              <p className="font-head text-xl font-bold text-[#3D405B] mt-1">{chapter.chapter_title}</p>
              <p className="text-sm text-[#6B7280] mt-2 line-clamp-3">{chapter.preview}…</p>
            </div>
            <p className="font-head font-semibold text-[#3D405B]">Create an assignment from this chapter</p>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
              {TYPES.map((t) => (
                <button key={t.key} data-testid={`studio-type-${t.key}`} onClick={() => generate(t.key)} disabled={busy}
                  className="flex items-center gap-3 rounded-xl bg-white border border-[#EAE6DF] p-4 text-left hover:-translate-y-1 hover:shadow-md hover:border-[#E07A5F] transition-[transform,box-shadow,border-color] disabled:opacity-50">
                  <t.icon size={20} className="text-[#E07A5F]" />
                  <span className="font-medium text-[#3D405B]">{t.key}</span>
                </button>
              ))}
            </div>
            {busy && <p className="text-sm text-[#6B7280]">Generating {activeType}…</p>}
            {created.length > 0 && (
              <div className="rounded-2xl bg-[#F2EFE9] border border-[#EAE6DF] p-5">
                <p className="text-sm font-semibold text-[#3D405B] mb-2">Published from this chapter</p>
                <ul className="space-y-1">{created.map((c, i) => <li key={i} className="text-sm text-[#6B7280] flex items-center gap-2"><CheckCircle2 size={14} className="text-[#81B29A]" /> {c.title}</li>)}</ul>
              </div>
            )}
            <button onClick={() => { setPhase("setup"); setChapter(null); setFiles([]); setCreated([]); }} className="text-sm text-[#6B7280] hover:text-[#3D405B]">Start a new chapter</button>
          </div>
        )}

        {phase === "review" && lesson && (
          <div data-testid="studio-review" className="space-y-6">
            <div className="rounded-2xl bg-white border border-[#EAE6DF] p-8">
              <span className="text-xs font-semibold uppercase tracking-wide text-[#81B29A]">{activeType} · review before publishing</span>
              <input value={lesson.chapter_title || ""} onChange={(e) => setLesson({ ...lesson, chapter_title: e.target.value })}
                className="mt-3 w-full font-head text-2xl font-bold text-[#3D405B] bg-transparent outline-none border-b border-transparent focus:border-[#EAE6DF]" />
              <textarea value={lesson.summary || ""} onChange={(e) => setLesson({ ...lesson, summary: e.target.value })}
                className="mt-4 w-full min-h-[120px] leading-relaxed text-[#3D405B] bg-[#F7F5F0] rounded-xl p-4 outline-none" />
              {lesson.key_facts?.length > 0 && (
                <div className="mt-4">
                  <p className="text-sm font-semibold text-[#3D405B] mb-2">Key Facts</p>
                  <ul className="list-disc pl-5 text-sm text-[#6B7280] space-y-1">{lesson.key_facts.map((f, i) => <li key={i}>{f}</li>)}</ul>
                </div>
              )}
              <p className="text-sm font-semibold text-[#3D405B] mt-6 mb-2">{(lesson.questions || []).length} questions</p>
              <div className="space-y-2">
                {(lesson.questions || []).map((q, i) => (
                  <div key={q.id || i} className="text-sm bg-[#F7F5F0] rounded-xl p-3">
                    <span className="stat text-xs text-[#81B29A]">{q.type === "multiple_choice" ? "MC" : "Written"} · {q.points}pt</span>
                    <p className="text-[#3D405B]">{i + 1}. {q.question}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              <button onClick={() => { setLesson(null); setPhase("ready"); }} className="rounded-full border border-[#EAE6DF] px-6 py-3 hover:border-[#3D405B] transition-colors">Discard</button>
              <button onClick={() => generate(activeType)} disabled={busy} className="rounded-full border border-[#EAE6DF] px-6 py-3 hover:border-[#E07A5F] transition-colors">Regenerate</button>
              <button data-testid="studio-publish-btn" onClick={publish} disabled={busy}
                className="flex items-center gap-2 rounded-full bg-[#81B29A] text-white px-8 py-3 font-semibold hover:bg-[#6f9d87] transition-colors disabled:opacity-60"><CheckCircle2 size={18} /> Publish {activeType}</button>
            </div>
          </div>
        )}
      </main>
      <style>{`.inp{margin-top:.25rem;width:100%;border-radius:.75rem;border:1px solid #EAE6DF;background:#fff;padding:.6rem .9rem;outline:none}.inp:focus{box-shadow:0 0 0 2px #E07A5F}.lbl{font-size:.875rem;font-weight:500;color:#3D405B}`}</style>
    </div>
  );
}
